import { useEffect, useState } from "react";
import { api, type CmsPageRow } from "../api.js";
import { useLocale } from "../ui.js";

type CmsLocale = "en" | "hi";

export function CmsPanel({ token }: { token: string }) {
  const { tr } = useLocale();
  const [pages, setPages] = useState<CmsPageRow[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editLocale, setEditLocale] = useState<CmsLocale>("en");
  const [draft, setDraft] = useState<CmsPageRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  function load() {
    void api.cmsPages().then((res) => {
      const list = res.ok && res.data ? res.data.pages : [];
      setPages(list);
      if (list.length > 0) {
        setActiveId((prev) => prev ?? list[0].id);
      }
    });
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (pages && activeId) {
      const page = pages.find((p) => p.id === activeId);
      setDraft(page ? structuredClone(page) : null);
      setSaved(false);
    }
  }, [pages, activeId]);

  async function save() {
    if (!draft) return;
    setBusy(true);
    setSaved(false);
    const res = await api.adminSaveCmsPage(token, { id: draft.id, en: draft.en, hi: draft.hi });
    setBusy(false);
    if (res.ok) {
      setSaved(true);
      load();
    }
  }

  function setContent(field: "title" | "subtitle" | "body", value: string) {
    setDraft((prev) => (prev ? { ...prev, [editLocale]: { ...prev[editLocale], [field]: value } } : prev));
    setSaved(false);
  }

  const content = draft ? draft[editLocale] : null;

  return (
    <section className="panel-card">
      <h2>{tr("cms")}</h2>
      <p className="section-desc">{tr("owner")} · bilingual EN + हिं</p>
      {pages === null ? (
        <p className="muted">{tr("loading")}</p>
      ) : (
        <>
          <div className="locale-toggle" style={{ flexWrap: "wrap", marginBottom: 14 }}>
            {pages.map((p) => (
              <button key={p.id} className={activeId === p.id ? "active" : ""} onClick={() => setActiveId(p.id)}>
                {p.id}
              </button>
            ))}
          </div>
          {content && draft ? (
            <>
              <div className="locale-toggle" style={{ marginBottom: 12 }}>
                <button className={editLocale === "en" ? "active" : ""} onClick={() => setEditLocale("en")}>
                  EN
                </button>
                <button className={editLocale === "hi" ? "active" : ""} onClick={() => setEditLocale("hi")}>
                  हिं
                </button>
              </div>
              <label>{tr("title")}</label>
              <input value={content.title} onChange={(e) => setContent("title", e.target.value)} />
              <label>{tr("subtitle")}</label>
              <input value={content.subtitle} onChange={(e) => setContent("subtitle", e.target.value)} />
              <label>{tr("body")}</label>
              <textarea className="form-textarea" value={content.body} onChange={(e) => setContent("body", e.target.value)} />
              <p className="muted">
                {tr("updatedBy")}: {draft.updatedBy ?? tr("never")}
              </p>
              {saved ? <div className="msg ok">{tr("saved")}</div> : null}
              <button className="btn-primary" disabled={busy} onClick={() => void save()}>
                {tr("save")}
              </button>
            </>
          ) : null}
        </>
      )}
    </section>
  );
}
